import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Gamification Strategy Generator. Learn how we collect, use, and protect your personal information."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/privacy-policy"
        keywords="privacy policy, data protection, personal information, gamification tool privacy"
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main className="pt-32 pb-20 px-4">
          <article className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            <header className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
              <p className="text-muted-foreground">Last updated: April 19, 2026</p>
            </header>

            <section className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to Gamification Strategy Generator ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We collect only the information needed to generate and deliver your personalised gamification strategy:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong>Assessment Inputs:</strong> Your name, email address, app type, experience intent, usage frequency, session duration, user motivations, main challenge, and sensory preferences</li>
                  <li><strong>Generated Strategy Output:</strong> The personalised gamification strategy produced from your inputs</li>
                  <li><strong>Payment Information:</strong> Processed securely via Stripe — we do not store your card details</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Data</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use your information solely to generate and deliver your personalised gamification strategy. Specifically:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Your inputs are <strong>never reviewed by us</strong> as part of normal operation</li>
                  <li>Your data is <strong>never sold or shared</strong> with third parties</li>
                  <li>Your data is <strong>never used to train AI models</strong></li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">4. How Your Data Is Stored</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your assessment inputs and generated strategy are stored securely in our database and are accessible only via your unique private token link. Direct database access is blocked for all end users — only you, using your private link, can access your results.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">5. Accessing Your Results</h2>
                <p className="text-muted-foreground leading-relaxed">
                  After purchase, your strategy is accessible via the private link emailed to you. You can revisit it as many times as you like for <strong>30 days from the date of purchase</strong>. Your strategy PDF is also attached to your confirmation email as a permanent copy you can keep indefinitely.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Assessment data and generated strategies are retained for <strong>30 days from the date of purchase</strong>, after which your private token link expires. The PDF copy attached to your confirmation email remains yours to keep.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">7. Third Parties</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Payment is processed by <strong>Stripe</strong>. No other third parties have access to your data. We do not sell or share your personal information with anyone.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">8. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement appropriate technical and organisational security measures to protect your personal information, including token-gated access controls and database-level access restrictions. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">9. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Object to or restrict processing</li>
                  <li>Data portability</li>
                  <li>Withdraw consent</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">10. Cookies and Tracking</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use essential cookies to enable basic functionality of our service. We may also use analytics cookies to understand how visitors interact with our website. You can control cookie preferences through your browser settings.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">11. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our service is not intended for children under 18 years of age. We do not knowingly collect personal information from children.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">12. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about this Privacy Policy or our data practices, please contact us at privacy@gamifyme.com.
                </p>
              </div>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;